import PrintIcon from "@mui/icons-material/Print";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { FC, ReactNode, useEffect, useState } from "react";

import usePrint from "../../hooks/usePrint";
import useFairs from "../../services/useFairs";
import useOrders from "../../services/useOrders";
import { IOrder } from "../../types/orders";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  UniqueIdentifier,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

const formatPhone = (phone: string) =>
  `(${phone.slice(3, 5)}) ${phone.slice(5, 10)}-${phone.slice(10, 14)}`;

const getActiveOrder = (activeId: UniqueIdentifier, routes: IOrder[][]) =>
  routes
    .find((route) => !!route.find((order) => order.id === activeId))
    ?.find((order) => order.id === activeId);

const getRouteIndex = (
  routes: IOrder[][],
  id: UniqueIdentifier,
  isContainer: boolean,
): number =>
  isContainer
    ? (id as number)
    : routes.findIndex((route) => route.some((order) => order.id === id));

const getOrderIndex = (route: IOrder[], id: UniqueIdentifier): number =>
  route.findIndex((order) => order.id === id);

interface DroppableProps {
  children?: ReactNode;
  index: number;
}

const Droppable: FC<DroppableProps> = ({ children, index }) => {
  const { setNodeRef } = useDroppable({ data: { container: true }, id: index });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: "34px",
      }}
    >
      {children}
    </Box>
  );
};

interface SortableItemProps {
  children?: ReactNode;
  id: number;
}

const SortableItem: FC<SortableItemProps> = ({ children, id }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id,
    });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        transition,
      }
    : undefined;
  return (
    <Box ref={setNodeRef} sx={style} {...listeners} {...attributes}>
      {children}
    </Box>
  );
};

interface OrderProps {
  isDragging?: boolean;
  isPrinting?: boolean;
  order: IOrder;
}

const Order: FC<OrderProps> = ({ isDragging = false, isPrinting, order }) => {
  return (
    <Box
      sx={{
        cursor: isDragging ? "grabbing" : "grab",
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        padding: "1rem",
      }}
    >
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <Box sx={{ flex: isPrinting ? "0 0 auto" : undefined }}>
          {order.urban_partner.name}
        </Box>
        <Box sx={{ flex: isPrinting ? "0 0 auto" : undefined }}>
          {formatPhone(order.urban_partner.phone)}
        </Box>
        <Box sx={{ flex: isPrinting ? "1 1 auto" : undefined }}>
          {order.address}
        </Box>
      </Box>
      {order.delivery_method_title && (
        <Box
          sx={{
            fontSize: "0.875rem",
            color: "text.secondary",
            fontStyle: "italic",
          }}
        >
          {order.delivery_method_title}
          {order.delivery_method_is_home_delivery
            ? " (Entrega)"
            : " (Retirada)"}
        </Box>
      )}
    </Box>
  );
};

interface RouteProps {
  index: number;
  onRemoveRoute: () => void;
  route: IOrder[];
}

const Route: FC<RouteProps> = ({ index, onRemoveRoute, route }) => {
  return (
    <Box>
      <Typography gutterBottom variant="h5" component="h1">
        Rota {index + 1}
      </Typography>
      <Box
        sx={{
          border: "1px solid black",
          borderRadius: "1rem",
        }}
      >
        <SortableContext
          items={route.map((order) => order.id)}
          key={index}
          strategy={verticalListSortingStrategy}
        >
          <Droppable index={index}>
            {route?.map((order, orderIndex) => (
              <Box key={order.id}>
                {orderIndex > 0 ? (
                  <Box sx={{ borderBottom: "1px solid black" }}></Box>
                ) : null}
                <SortableItem id={order.id}>
                  <Order order={order} />
                </SortableItem>
              </Box>
            ))}
          </Droppable>
        </SortableContext>
      </Box>
      {index !== 0 ? (
        <Button disabled={route.length > 0} onClick={onRemoveRoute}>
          Remove route
        </Button>
      ) : null}
    </Box>
  );
};

const DeliveryRoute = () => {
  const { data: fairs } = useFairs();
  const { data: orders } = useOrders(fairs ? fairs[0].id : null);
  const [routes, setRoutes] = useState<null | IOrder[][]>(null);
  const [activeId, setActiveId] = useState<null | UniqueIdentifier>(null);
  const { printing, print } = usePrint();

  useEffect(() => {
    if (orders && routes === null) {
      setRoutes([[...orders]]);
    }
  }, [orders, routes, setRoutes]);

  if (printing) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        {routes?.map((route, index) => (
          <Box>
            <Typography gutterBottom variant="h5" component="h1">
              Rota {index + 1}
            </Typography>
            <Box
              sx={{
                border: "1px solid black",
                borderRadius: "1rem",
              }}
            >
              {route?.map((order, orderIndex) => (
                <Box
                  key={order.id}
                  sx={{
                    "&:nth-of-type(even)": {
                      backgroundColor: "#dddddd",
                    },
                  }}
                >
                  {orderIndex > 0 ? (
                    <Box sx={{ borderBottom: "1px solid black" }}></Box>
                  ) : null}
                  <Order isPrinting order={order} />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  const addRoute = () => {
    if (routes) {
      setRoutes([...routes, []]);
    }
  };

  const removeRoute = (index: number) => () => {
    if (routes) {
      setRoutes([...routes.slice(0, index), ...routes.slice(index + 1)]);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const { id } = active;

    if (!over) {
      return;
    }

    if (!routes) {
      return;
    }

    const { data, id: overId } = over;

    const activeContainerIndex = getRouteIndex(routes, id, false);
    const overContainerIndex = getRouteIndex(
      routes,
      overId,
      data.current?.container,
    );

    if (
      activeContainerIndex === -1 ||
      overContainerIndex === -1 ||
      activeContainerIndex !== overContainerIndex
    ) {
      return;
    }

    const activeItems = routes[activeContainerIndex];
    const overItems = routes[overContainerIndex];

    const activeIndex = getOrderIndex(activeItems, id);
    const overIndex = getOrderIndex(overItems, overId);

    if (activeIndex !== overIndex) {
      setRoutes([
        ...routes.slice(0, activeContainerIndex),
        arrayMove(activeItems, activeIndex, overIndex),
        ...routes.slice(activeContainerIndex + 1),
      ]);
    }

    setActiveId(null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    const { id } = active;

    if (!over) {
      return;
    }

    if (!routes) {
      return;
    }

    const { data, id: overId } = over;

    const activeContainerIndex = getRouteIndex(routes, id, false);
    const overContainerIndex = getRouteIndex(
      routes,
      overId,
      data.current?.container,
    );

    if (
      activeContainerIndex === -1 ||
      overContainerIndex === -1 ||
      activeContainerIndex === overContainerIndex
    ) {
      return;
    }

    const activeItems = routes[activeContainerIndex];
    const overItems = routes[overContainerIndex];

    const activeIndex = getOrderIndex(activeItems, id);
    const overIndex = getOrderIndex(overItems, overId);

    const newIndex = data.current?.container ? overItems.length + 1 : overIndex;

    const activeListWithoutActive = [
      ...routes[activeContainerIndex].slice(0, activeIndex),
      ...routes[activeContainerIndex].slice(activeIndex + 1),
    ];

    const activeOrder = getActiveOrder(id, routes);

    if (!activeOrder) {
      return;
    }

    const overListWithActive = [
      ...routes[overContainerIndex].slice(0, newIndex),
      activeOrder,
      ...routes[overContainerIndex].slice(newIndex),
    ];

    let updatedItemsLists = [
      ...routes.slice(0, activeContainerIndex),
      activeListWithoutActive,
      ...routes.slice(activeContainerIndex + 1),
    ];

    updatedItemsLists = [
      ...updatedItemsLists.slice(0, overContainerIndex),
      overListWithActive,
      ...updatedItemsLists.slice(overContainerIndex + 1),
    ];

    setRoutes(updatedItemsLists);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const { id } = active;

    setActiveId(id);
  };

  const activeOrder =
    activeId && routes ? getActiveOrder(activeId, routes) : null;

  return (
    <Box
      sx={{
        margin: "1.5rem auto",
        maxWidth: "900px",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography gutterBottom variant="h4" component="h1">
          Rotas de Entrega
        </Typography>
        <IconButton
          size="large"
          color="inherit"
          aria-label="print"
          onClick={print}
        >
          <PrintIcon />
        </IconButton>
      </Box>
      <DndContext
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          {routes?.map((route, index) => (
            <Route
              index={index}
              key={index}
              onRemoveRoute={removeRoute(index)}
              route={route}
            />
          ))}
        </Box>
        <DragOverlay>
          {activeOrder ? <Order isDragging order={activeOrder} /> : null}
        </DragOverlay>
      </DndContext>
      <Button onClick={addRoute}>Add route</Button>
    </Box>
  );
};

export default DeliveryRoute;
